import React, { useState, useEffect } from 'react';
import { Modal, Input, Form, Tree, Button, Space, message, Spin } from 'antd';
import type { DataNode } from 'antd/es/tree';
import {
  FolderOutlined,
  FileOutlined,
  FolderAddOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { filesService, FileNode } from '../api';
import { createContextLogger } from '../utils/logger';

const logger = createContextLogger('FileBrowserDialog');

// Type definitions
export interface FileBrowserDialogProps {
  open: boolean;
  onCancel: () => void;
  onSave: (path: string) => Promise<void>;
  initialPath?: string;
  title?: string;
  fileExtension?: string;
  mode?: 'save' | 'open';
}

interface FileTreeNode extends DataNode {
  title: string;
  key: string;
  children?: FileTreeNode[];
  isLeaf?: boolean;
  path: string;
  type: 'file' | 'folder';
}

interface FormValues {
  filename: string;
}

const FileBrowserDialog: React.FC<FileBrowserDialogProps> = ({ 
  open, 
  onCancel, 
  onSave, 
  initialPath = '', 
  title,
  fileExtension = '',
  mode = 'save'
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [fileTree, setFileTree] = useState<FileTreeNode[]>([]);
  const [selectedPath, setSelectedPath] = useState<string>('');
  const [selectedFolder, setSelectedFolder] = useState<string>('');
  const [newFolderModalVisible, setNewFolderModalVisible] = useState<boolean>(false);
  const [newFolderName, setNewFolderName] = useState<string>('');

  // Load file tree from backend when modal opens
  useEffect(() => {
    if (open) {
      loadFileTree();
      
      // Set initial path if provided
      if (initialPath) {
        const parts = initialPath.split('/');
        const filename = parts.pop() || '';
        const folder = parts.join('/') || '/';
        
        form.setFieldsValue({ filename: filename.replace(fileExtension, '') });
        setSelectedFolder(folder);
        setSelectedPath(folder);
      }
    }
  }, [open, initialPath, fileExtension, form]);

  // Uses the same endpoint as the sidebar FileBrowser component
  const loadFileTree = async (): Promise<void> => {
    setLoading(true);
    try {
      const fileList = await filesService.getFileList();
      const treeData = convertToTreeData(fileList);
      setFileTree(treeData);
      
      // If no selected folder, select root
      if (!selectedFolder) {
        setSelectedFolder('/');
        setSelectedPath('/');
      }
    } catch (error) {
      logger.error('Failed to load file tree:', error);
      message.error(t('fileBrowser.failedToLoad'));
      
      // Fallback to empty tree with root
      setFileTree([{
        title: 'Root',
        key: '/',
        path: '/',
        type: 'folder',
        children: [],
      }]);
    } finally {
      setLoading(false);
    }
  };

  const convertToTreeData = (node: FileNode, parentPath = ''): FileTreeNode[] => {
    if (!node) return [];

    const currentPath = parentPath ? `${parentPath}/${node.name}` : node.name || '/';
    
    if (node.isFolder) {
      const children = node.children 
        ? node.children.flatMap((child: FileNode) => convertToTreeData(child, currentPath))
        : [];

      return [{
        title: node.name || 'Root',
        key: currentPath,
        path: currentPath,
        type: 'folder',
        children,
        isLeaf: false,
      }];
    } else {
      return [{
        title: node.name,
        key: currentPath,
        path: currentPath,
        type: 'file',
        isLeaf: true,
      }];
    }
  };

  const handleAddFolder = async (): Promise<void> => {
    if (!newFolderName) {
      message.warning(t('fileBrowser.enterFolderName'));
      return;
    }

    try {
      // Create folder on backend
      const newFolderPath = `${selectedFolder}/${newFolderName}`.replace('//', '/');
      
      // For now, we'll create a placeholder file to create the directory
      // Most file systems need a file to create a directory structure
      await filesService.createFile(`${newFolderPath}/.gitkeep`, 'text');
      
      message.success(t('fileBrowser.folderCreated'));
      setNewFolderModalVisible(false);
      setNewFolderName('');
      
      // Reload file tree
      await loadFileTree();
    } catch (error) {
      logger.error('Failed to create folder:', error);
      message.error(t('fileBrowser.failedToCreateFolder'));
    }
  };

  const handleOk = async (): Promise<void> => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      
      // Construct full path
      let filename = values.filename;
      if (fileExtension && !filename.endsWith(fileExtension)) {
        filename += fileExtension;
      }
      
      const fullPath = `${selectedFolder}/${filename}`.replace('//', '/');
      
      await onSave(fullPath);
      form.resetFields();
      onCancel();
    } catch (error) {
      if (error instanceof Error) {
        message.error(t('fileBrowser.failedToSave') + ': ' + error.message);
      } else {
        message.error(t('fileBrowser.failedToSave'));
      }
      logger.error('Save failed:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSelect = (selectedKeys: React.Key[], info: any): void => {
    if (selectedKeys.length === 0) return;
    
    const key = selectedKeys[0] as string;
    const node = info.node as FileTreeNode;
    
    setSelectedPath(key);
    
    if (node.type === 'folder') {
      setSelectedFolder(key);
    } else if (node.type === 'file') {
      // If a file is selected, extract the folder and filename
      const parts = key.split('/');
      const filename = parts.pop() || '';
      const folder = parts.join('/') || '/';
      
      setSelectedFolder(folder);
      
      if (mode === 'open') {
        // In open mode, just set the path
        setSelectedPath(key);
      } else {
        // In save mode, populate the filename field
        form.setFieldsValue({ 
          filename: filename.replace(fileExtension, '') 
        });
      }
    }
  };

  const renderTreeNode = (node: FileTreeNode): React.ReactNode => {
    return (
      <Space>
        {node.type === 'folder' ? <FolderOutlined /> : <FileOutlined />}
        {node.title}
      </Space>
    );
  };

  return (
    <>
      <Modal
        title={title || (mode === 'save' ? t('fileBrowser.saveAs') : t('fileBrowser.open'))}
        open={open}
        onCancel={onCancel}
        onOk={handleOk}
        okText={mode === 'save' ? t('fileBrowser.save') : t('fileBrowser.open')}
        confirmLoading={saving}
        width={700}
        bodyStyle={{ maxHeight: '600px', overflowY: 'auto' }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {mode === 'save' && (
            <div style={{ marginBottom: 8 }}>
              <Button 
                icon={<FolderAddOutlined />} 
                onClick={() => setNewFolderModalVisible(true)}
                disabled={loading}
              >
                {t('fileBrowser.newFolder')}
              </Button>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
              <div style={{ marginTop: 16 }}>{t('fileBrowser.loading')}</div>
            </div>
          ) : (
            <div style={{ 
              border: '1px solid #f0f0f0', 
              borderRadius: '4px', 
              padding: '12px',
              maxHeight: '300px',
              overflowY: 'auto'
            }}>
              <Tree
                treeData={fileTree}
                selectedKeys={[selectedPath]}
                onSelect={handleSelect}
                showIcon={false}
                titleRender={renderTreeNode}
                defaultExpandAll={fileTree.length < 20}
              />
            </div>
          )}

          {mode === 'save' && (
            <Form form={form} layout="vertical" initialValues={{ filename: '' }}>
              <Form.Item
                name="filename"
                label={t('fileBrowser.filename')}
                rules={[
                  { required: true, message: t('fileBrowser.filenameRequired') },
                  {
                    pattern: /^[a-zA-Z0-9-_. ]+$/,
                    message: t('fileBrowser.invalidFilename'),
                  },
                ]}
              >
                <Input 
                  placeholder={t('fileBrowser.filenamePlaceholder')} 
                  addonBefore={selectedFolder}
                  addonAfter={fileExtension}
                />
              </Form.Item>
            </Form>
          )}

          {mode === 'open' && selectedPath && (
            <div style={{ padding: '8px', background: '#f5f5f5', borderRadius: '4px' }}>
              <strong>{t('fileBrowser.selectedPath')}:</strong> {selectedPath}
            </div>
          )}
        </Space>
      </Modal>

      <Modal
        title={t('fileBrowser.newFolder')}
        open={newFolderModalVisible}
        onCancel={() => {
          setNewFolderModalVisible(false);
          setNewFolderName('');
        }}
        onOk={handleAddFolder}
      >
        <Input
          placeholder={t('fileBrowser.enterFolderNamePlaceholder')}
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
          onPressEnter={handleAddFolder}
        />
      </Modal>
    </>
  );
};

export default FileBrowserDialog;

